import React, { useState, useCallback } from 'react';
import { Modal, Button, Spinner, Card, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faArrowUp, faArrowDown, faTimes, faSave, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { toast } from 'react-toastify';
import { CampaignService } from '../../Services/Axios/Api';
import './CampaignOrderEditor.css';

const ItemType = {
    CAMPAIGN: 'campaign',
};

const DraggableCampaign = ({ campaign, index, moveCampaign, onMoveUp, onMoveDown, isFirst, isLast }) => {
    const [, ref] = useDrag({
        type: ItemType.CAMPAIGN,
        item: { index },
    });

    const [, drop] = useDrop({
        accept: ItemType.CAMPAIGN,
        hover: (draggedItem) => {
            if (draggedItem.index !== index) {
                moveCampaign(draggedItem.index, index);
                draggedItem.index = index;
            }
        },
    });

    return (
        <div ref={(node) => ref(drop(node))} className="campaign-order-item">
            <Card>
                <Card.Body className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center flex-grow-1">
                        <div className="drag-handle me-3">
                            <FontAwesomeIcon icon={faGripVertical} className="text-muted" />
                        </div>
                        <div className="flex-grow-1">
                            <h6 className="mb-1">{campaign.name}</h6>
                            <small className="text-muted">
                                {campaign.order !== undefined ? (
                                    <>
                                        Current Order: {campaign.order} 
                                        {campaign.order !== index + 1 && (
                                            <span className="text-warning ms-2">→ New Order: {index + 1}</span>
                                        )}
                                    </>
                                ) : (
                                    <span className="text-info">New Order: {index + 1}</span>
                                )}
                            </small>
                        </div>
                    </div>
                    <div className="d-flex gap-2">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={onMoveUp}
                            disabled={isFirst}
                            title="Move Up"
                        >
                            <FontAwesomeIcon icon={faArrowUp} />
                        </Button>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={onMoveDown}
                            disabled={isLast}
                            title="Move Down"
                        >
                            <FontAwesomeIcon icon={faArrowDown} />
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

const CampaignOrderEditor = ({ show, onHide, campaigns, onSave }) => {
    const [orderedCampaigns, setOrderedCampaigns] = useState([]);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [showConfirmClose, setShowConfirmClose] = useState(false);

    // Initialize ordered campaigns when modal opens or campaigns change
    React.useEffect(() => {
        if (show && campaigns && campaigns.length > 0) {
            // Sort campaigns by order first, then by ID as fallback
            const sorted = [...campaigns].sort((a, b) => {
                // If both have order, sort by order
                if (a.order !== undefined && b.order !== undefined) {
                    if (a.order !== b.order) {
                        return a.order - b.order;
                    }
                }
                // If only one has order, prioritize it
                if (a.order !== undefined && b.order === undefined) {
                    return -1;
                }
                if (a.order === undefined && b.order !== undefined) {
                    return 1;
                }
                // If both don't have order or same order, sort by ID
                return (a.id || 0) - (b.id || 0);
            });
            setOrderedCampaigns(sorted);
            setHasChanges(false);
        }
    }, [show, campaigns]);

    const moveCampaign = useCallback((dragIndex, hoverIndex) => {
        setOrderedCampaigns(prev => {
            const newOrder = [...prev];
            const draggedItem = newOrder[dragIndex];
            newOrder.splice(dragIndex, 1);
            newOrder.splice(hoverIndex, 0, draggedItem);
            setHasChanges(true);
            return newOrder;
        });
    }, []);

    const moveCampaignUp = useCallback((index) => {
        if (index === 0) return;
        setOrderedCampaigns(prev => {
            const newOrder = [...prev];
            [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
            setHasChanges(true);
            return newOrder;
        });
    }, []);

    const moveCampaignDown = useCallback((index) => {
        setOrderedCampaigns(prev => {
            if (index === prev.length - 1) return prev;
            const newOrder = [...prev];
            [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
            setHasChanges(true);
            return newOrder;
        });
    }, []);

    const handleSave = async () => {
        if (!hasChanges) {
            onHide();
            return;
        }

        try {
            setSaving(true);
            // Prepare data: array of objects with id and order
            const orderData = orderedCampaigns.map((campaign, index) => ({
                id: campaign.id,
                order: index + 1
            }));

            await CampaignService.updateCampaignOrder(orderData);
            toast.success('Campaign order updated successfully');
            
            if (onSave) {
                onSave();
            }
            
            setHasChanges(false);
            onHide();
        } catch (error) {
            toast.error('Failed to update campaign order');
            console.error('Error updating campaign order:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (hasChanges) {
            setShowConfirmClose(true);
            return;
        }
        setHasChanges(false);
        onHide();
    };

    const handleConfirmClose = () => {
        setShowConfirmClose(false);
        setHasChanges(false);
        onHide();
    };

    const handleCancelClose = () => {
        setShowConfirmClose(false);
    };

    return (
        <>
        <Modal 
            show={show} 
            onHide={handleClose} 
            size="lg" 
            centered
            backdrop="static"
            className="campaign-order-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title>Edit Campaign Order</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <p className="text-muted mb-3">
                        Drag and drop campaigns to reorder them, or use the arrow buttons to move items up or down.
                    </p>
                    {hasChanges && (
                        <div className="alert alert-info mb-3">
                            <small>You have unsaved changes. Click Save to apply them.</small>
                        </div>
                    )}
                </div>
                
                <DndProvider backend={HTML5Backend}>
                    <div className="campaign-order-list">
                        {orderedCampaigns.length === 0 ? (
                            <div className="text-center text-muted py-5">
                                <p>No campaigns to reorder.</p>
                            </div>
                        ) : (
                            orderedCampaigns.map((campaign, index) => (
                                <DraggableCampaign
                                    key={campaign.id}
                                    campaign={campaign}
                                    index={index}
                                    moveCampaign={moveCampaign}
                                    onMoveUp={() => moveCampaignUp(index)}
                                    onMoveDown={() => moveCampaignDown(index)}
                                    isFirst={index === 0}
                                    isLast={index === orderedCampaigns.length - 1}
                                />
                            ))
                        )}
                    </div>
                </DndProvider>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={saving}>
                    <FontAwesomeIcon icon={faTimes} className="me-2" />
                    Cancel
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleSave}
                    disabled={saving || !hasChanges}
                >
                    {saving ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faSave} className="me-2" />
                            Save Order
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>

        {/* Confirmation Modal for Unsaved Changes */}
        <Modal 
            show={showConfirmClose} 
            onHide={handleCancelClose}
            centered
            backdrop="static"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-2" />
                    Unsaved Changes
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="warning" className="mb-0">
                    You have unsaved changes. Are you sure you want to close? All changes will be lost.
                </Alert>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleCancelClose}>
                    <FontAwesomeIcon icon={faTimes} className="me-2" />
                    Cancel
                </Button>
                <Button variant="warning" onClick={handleConfirmClose}>
                    <FontAwesomeIcon icon={faTimes} className="me-2" />
                    Close Without Saving
                </Button>
            </Modal.Footer>
        </Modal>
        </>
    );
};

export default CampaignOrderEditor;

